import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { Button, Paper, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { api } from "@api/api";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";
import { CondicaoContext } from "../../../contexto";
import { lerSolicitacaoAtiva, marcarEnvio, concluirSolicitacao } from "./solicitacoesProcesso";
import FormularioDocumentosProcesso from "./FormularioDocumentosProcesso";
import ConfirmacaoRegistroProcesso from "./ConfirmacaoRegistroProcesso";

export default function Documentos({ onReturn, onRegister }) {
  const { userId } = useContext(CondicaoContext);
  const envioRef = useRef(false);
  const [documents, setDocuments] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [processType, setProcessType] = useState("");
  const [fileInputs, setFileInputs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [erroDocumentos, setErroDocumentos] = useState(false);
  const [solicitacao, setSolicitacao] = useState(null);
  const [resultado, setResultado] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;
    let ativo = true;
    const controller = new AbortController();
    const carregar = async () => {
      try {
        const dados = lerSolicitacaoAtiva(userId);
        if (!dados?.chaveSolicitacao) throw new Error('Volte e selecione os dados do processo para continuar.');
        const atual = { ...dados, userId };
        setSolicitacao(atual);
        setProcessType(atual.processType);
        if (atual.envioIniciado) {
          setIsChecking(true);
          try {
            const response = await axios.get(api.processRequest(atual.chaveSolicitacao), {
              withCredentials: true, timeout: 15000, signal: controller.signal,
            });
            if (!ativo) return;
            if (aplicarResultado(response.data, atual)) return;
          } catch (error) {
            if (!ativo) return;
            avisar('Não foi possível confirmar a tentativa anterior. Você pode verificar novamente ou reenviar os mesmos documentos.', 'warning');
          } finally {
            if (ativo) setIsChecking(false);
          }
        }
        if (ativo) await fetchDocuments(atual.processType, atual.envioIniciado ? atual.chaveSolicitacao : undefined);
      } catch (error) {
        if (ativo) avisar(error.message, 'error');
      }
    };
    carregar();
    return () => { ativo = false; controller.abort(); };
    // A solicitação é carregada uma vez para cada sessão autenticada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    // Rascunhos anteriores ainda não guardavam o nome selecionado.
    if (!solicitacao?.processType || solicitacao.nomeProcesso) return;
    const controller = new AbortController();
    axios.get(api.listGeneralProcesses(), {
      withCredentials: true, timeout: 15000, signal: controller.signal,
    }).then(({ data }) => {
      const tipo = Array.isArray(data) && data.find((item) => String(item.id) === String(solicitacao.processType));
      if (tipo?.nome) setSolicitacao((atual) => ({ ...atual, nomeProcesso: tipo.nome }));
    }).catch(() => {
      // A indisponibilidade do nome não invalida a confirmação do registro.
    });
    return () => controller.abort();
  }, [solicitacao?.processType, solicitacao?.nomeProcesso]);

  const [quantidadeDocumentos, setQuantidadeDocumentos] = useState(null);

  useEffect(() => {
    if (resultado?.estado !== 'concluida') return;
    if (Array.isArray(resultado.ids)) {
      setQuantidadeDocumentos(resultado.ids.length);
      return;
    }
    // Retomadas consultam os anexos do processo, nunca o catálogo atual.
    const controller = new AbortController();
    axios.get(api.getProcessDocs(resultado.processo.id), {
      withCredentials: true, timeout: 15000, signal: controller.signal,
    }).then(({ data }) => {
      if (!controller.signal.aborted && Array.isArray(data)) setQuantidadeDocumentos(data.length);
    }).catch(() => {
      // A contagem indisponível não invalida o registro confirmado.
    });
    return () => controller.abort();
  }, [resultado]);

  const avisar = (mensagem, severity = 'error') => {
    setSnackbarMessage(mensagem);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const aplicarResultado = (dados, tentativa) => {
    if (!['concluida', 'encerrada'].includes(dados?.estado) || !dados.processo?.id) return false;
    // Falha de storage não transforma um sucesso confirmado em erro de envio.
    try { concluirSolicitacao(tentativa); } catch (error) { console.error('Erro ao limpar o rascunho confirmado:', error); }
    setResultado(dados);
    return true;
  };

  const verificarResultado = async () => {
    if (envioRef.current || !solicitacao) return;
    envioRef.current = true;
    setIsChecking(true);
    try {
      const response = await axios.get(api.processRequest(solicitacao.chaveSolicitacao), {
        withCredentials: true, timeout: 15000,
      });
      if (!aplicarResultado(response.data, solicitacao)) {
        avisar('Envio completo ainda não confirmado. Anexe os documentos e tente novamente; a mesma solicitação será reutilizada.', 'info');
      }
    } catch {
      avisar('Não foi possível consultar o resultado agora. Tente novamente em instantes.', 'warning');
    } finally {
      envioRef.current = false;
      setIsChecking(false);
    }
  };

  const fetchDocuments = async (processId, chaveSolicitacao) => {
    setDocumentsLoaded(false);
    setErroDocumentos(false);
    try {
      const response = await axios.get(api.fetchDocuments(processId, chaveSolicitacao), {
        withCredentials: true, timeout: 15000,
      });

      if (!Array.isArray(response.data)) throw new Error('Lista de documentos inválida.');
      setDocuments(response.data);
      setDocumentsLoaded(true);
    } catch (error) {
      setErroDocumentos(true);
      console.error("Erro ao buscar documentos:", error);
      avisar('Não foi possível carregar os documentos. Use "Recarregar documentos" para tentar novamente.');
    }
  };

  const handleUpload = (id, event) => {
    if (envioRef.current) return;
    const file = event.target.files[0];

    if (file) {
      if (file.type !== "application/pdf") {
        setSnackbarMessage("Apenas arquivos PDF são permitidos!");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      setUploadedDocs((prev) => ({
        ...prev,
        [id]: {
          uploaded: true,
          name: file.name,
          size: file.size,
        },
      }));

      setFileInputs((prev) => ({
        ...prev,
        [id]: file,
      }));
    }
  };

  const handleRemoveFile = (id) => {
    if (envioRef.current) return;
    setUploadedDocs((prev) => {
      const newUploadedDocs = { ...prev };
      delete newUploadedDocs[id];
      return newUploadedDocs;
    });

    setFileInputs((prev) => {
      const newFileInputs = { ...prev };
      delete newFileInputs[id];
      return newFileInputs;
    });
  };

  const allDocumentsUploaded = documentsLoaded && documents.every(
    (doc) => uploadedDocs[doc.id]?.uploaded,
  );

  const handleRegister = async () => {
    if (envioRef.current || isChecking) return;

    if (!allDocumentsUploaded || !isConfirmed) {
      setSnackbarMessage(
        "Todos os documentos devem ser anexados e confirmados.",
      );
      setSnackbarOpen(true);
      return;
    }

    if (!solicitacao?.chaveSolicitacao) {
      setSnackbarMessage("Erro: Dados do processo não encontrados.");
      setSnackbarOpen(true);
      return;
    }

    envioRef.current = true;
    setIsSubmitting(true);
    try {
      const atual = marcarEnvio(solicitacao);
      setSolicitacao(atual);
      const formData = new FormData();
      formData.append('processType', atual.processType);
      formData.append('description', '');
      formData.append('criadoEm', atual.criadoEm);
      formData.append('chaveSolicitacao', atual.chaveSolicitacao);
      if (atual.selectedSchool) formData.append('selectedSchool', atual.selectedSchool);
      documents.forEach((doc) => {
        formData.append('files', fileInputs[doc.id]);
        formData.append('documento_id[]', doc.id);
      });
      const response = await axios.post(api.registerProcess(), formData, {
        withCredentials: true, timeout: 60000,
      });
      if (!aplicarResultado(response.data, atual)) throw new Error('Resposta sem confirmação do envio. Verifique o resultado antes de reenviar.');
    } catch (error) {
      console.error("Erro ao registrar processo:", error);

      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message ||
          (!error.response ? 'A resposta não foi confirmada. Use "Verificar resultado" ou tente novamente com os mesmos documentos.' : error.message) ||
          "Erro ao registrar o processo.",
      );
      setSnackbarOpen(true);
    } finally {
      envioRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleReturn = () => {
    if (envioRef.current || isChecking) return;

    navigate("/novoProcesso", { state: { retomarSelecao: true } });
  };

  if (resultado?.estado === 'concluida') {
    return (
      <TransicaoPagina variante="navegacao">
        <ConfirmacaoRegistroProcesso resultado={resultado} solicitacao={solicitacao}
          quantidadeDocumentos={quantidadeDocumentos} />
      </TransicaoPagina>
    );
  }

  if (resultado) {
    const nomeProcesso = solicitacao?.nomeProcesso;
    return (
      <TransicaoPagina variante="navegacao">
        <Paper sx={{ p: 3, mt: 2 }}>
          <Alert severity={resultado.estado === 'concluida' ? 'success' : 'info'}>
            {resultado.estado === 'concluida'
              ? `Processo${nomeProcesso ? ` “${nomeProcesso}”` : ''} e documentos registrados com sucesso.`
              : `A solicitação do processo${nomeProcesso ? ` “${nomeProcesso}”` : ''} já foi finalizada e o registro foi encerrado. Consulte o histórico.`}
          </Alert>
          <Button sx={{ mt: 2, mr: 2 }} variant="contained" onClick={() => navigate('/novoProcesso')}>Criar outro processo</Button>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/home/processos')}>Ver meus processos</Button>
        </Paper>
      </TransicaoPagina>
    );
  }

  return (
    <TransicaoPagina variante="entrada" style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
      <FormularioDocumentosProcesso
        solicitacao={solicitacao}
        documents={documents}
        uploadedDocs={uploadedDocs}
        documentsLoaded={documentsLoaded}
        erroDocumentos={erroDocumentos}
        processType={processType}
        isChecking={isChecking}
        isSubmitting={isSubmitting}
        isConfirmed={isConfirmed}
        allDocumentsUploaded={allDocumentsUploaded}
        onVerify={verificarResultado}
        onReload={() => fetchDocuments(processType, solicitacao?.envioIniciado ? solicitacao.chaveSolicitacao : undefined)}
        onUpload={handleUpload}
        onRemove={handleRemoveFile}
        onConfirmar={() => setIsConfirmed((prev) => !prev)}
        onRegister={handleRegister}
        onReturn={handleReturn}
      />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
    </TransicaoPagina>
  );
}
